/**
 * Lightweight i18n system for the boulder gym app.
 * Provides LanguageProvider context, useTranslation hook, and translation helpers.
 */
import React, { createContext, useContext, useState, useCallback, useMemo } from "react";
import hu from "./hu";
import en from "./en";
import { DEFAULT_DIFF_KEYS, DEFAULT_HOLD_KEYS, SUPPORTED_LANGS } from "@sendly/shared";
import { localStore } from "../utils/safeStorage";

const TRANSLATIONS = { hu, en };
const DEFAULT_LANG = "hu";
const STORAGE_KEY = "sendly-lang";


function getInitialLang() {
  const stored = localStore.get(STORAGE_KEY);
  if (stored && SUPPORTED_LANGS.includes(stored)) return stored;
  // First-time visitor: auto-detect from browser. `navigator.language` returns
  // tags like "en-US" or "hu-HU" — we take the primary subtag. Fall back to
  // the default if the detected language isn't supported.
  try {
    const primary = (navigator.language || "").toLowerCase().split("-")[0];
    if (primary && SUPPORTED_LANGS.includes(primary)) return primary;
  } catch {}
  return DEFAULT_LANG;
}

function translate(lang, key, params) {
  const dict = TRANSLATIONS[lang] || TRANSLATIONS[DEFAULT_LANG];
  let str = dict[key] ?? TRANSLATIONS[DEFAULT_LANG][key] ?? key;
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      const value = String(v);
      // A Hungarian short date already ends in a period ("okt. 28."), and a
      // sentence-final "{date}." in the string would print it twice. The
      // template's period is dropped when the value brings its own. A function
      // replacer also stops "$&" / "$`" in a value from being read as a pattern.
      const endsWithPeriod = /[^.]\.$/.test(value);
      str = str.replace(new RegExp("\\{" + k + "\\}(\\.)?", "g"), (match, dot) => (
        dot && !endsWithPeriod ? value + dot : value
      ));
    });
  }
  return str;
}

const LanguageContext = createContext({
  lang: DEFAULT_LANG,
  setLang: () => {},
});

/**
 * LanguageProvider — the app's language, and the surfaces that override it.
 *
 * Mounted once at the root (`src/index.js`) it owns the session language:
 * initial value from localStorage / the browser, changed from the settings
 * screen. Nested with a `forceLang`, it pins a language for its subtree
 * instead — which is what a wall TV and an embedded widget need, because
 * nobody is logged in there and the browser's locale is meaningless. The
 * gym admin picks that language per screen (`dashboardSettings.tvLanguage`),
 * or it arrives as the widget's `?lang=` parameter.
 *
 * Forcing it here rather than at each `useTranslation(overrideLang)` call is
 * what lets a shared component be shared: `<TvLeaderboard>` renders on the
 * wall and in the app, and reads the right language in both without either
 * side handing it a `t`. Before this the override lived in the CALL, so the
 * only way down was to pass `t` as a prop the whole way — and a component
 * that forgot silently fell back to the browser language. That regression
 * shipped twice (`09414c8`, `67c502a`).
 *
 * @param {string} [forceLang] - Pin this language for the subtree. Ignored
 *   when unsupported or absent, which is the root provider's case.
 */
export function LanguageProvider({ children, forceLang }) {
  const forced = SUPPORTED_LANGS.includes(forceLang) ? forceLang : null;
  const outer = useContext(LanguageContext);
  // A pinned subtree never reads its own stored language, so don't go to
  // localStorage for one. The initializer runs once, so the branch is read
  // at mount — `forceLang` switching later moves `lang`, not this slot.
  const [ownLang, setLangState] = useState(() => (SUPPORTED_LANGS.includes(forceLang) ? DEFAULT_LANG : getInitialLang()));

  const setOwnLang = useCallback((newLang) => {
    if (!SUPPORTED_LANGS.includes(newLang)) return;
    setLangState(newLang);
    localStore.set(STORAGE_KEY, newLang);
  }, []);

  // Inside a pinned subtree the setter stays the one from the enclosing
  // provider: a switcher rendered there still changes the app's language,
  // it just doesn't repaint the wall. This matches what
  // `useTranslation(overrideLang)` has always returned.
  const outerSetLang = outer.setLang;
  const value = useMemo(
    () => (forced ? { lang: forced, setLang: outerSetLang } : { lang: ownLang, setLang: setOwnLang }),
    [forced, outerSetLang, ownLang, setOwnLang]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

/**
 * useTranslation hook.
 * @param {string} [overrideLang] - Optional language override (e.g. for TV dashboard)
 * @returns {{ lang: string, setLang: function, t: function }}
 */
export function useTranslation(overrideLang) {
  const ctx = useContext(LanguageContext);
  const lang = (overrideLang && SUPPORTED_LANGS.includes(overrideLang)) ? overrideLang : ctx.lang;
  const t = useCallback((key, params) => translate(lang, key, params), [lang]);
  return { lang, setLang: ctx.setLang, t };
}

/**
 * Translate outside React (a util that builds a user-facing string without a
 * `t` in hand, e.g. `callableErrorText`). Uses the language the provider
 * persists, so it matches what the user sees; a surface that pins its own
 * language with `forceLang` (wall TV, widget) is not covered.
 */
export function tStandalone(key, params) {
  return translate(getInitialLang(), key, params);
}

/**
 * `t()` with a fallback. `translate()` returns the KEY when a string is missing,
 * so a value that reaches the UI from data (an audit entry's field name, a log
 * entry type written by a newer server) would render "opsLog.field.whatever" at
 * the user. This degrades to the raw value instead, which at least reads as
 * data rather than as a bug.
 */
export function tOr(t, key, fallback) {
  const s = t(key);
  return s === key ? fallback : s;
}

/**
 * Translate a default difficulty label. Returns translated label for known defaults,
 * or the original label for custom (admin-added) difficulties.
 */
export function tDiffLabel(t, key, fallbackLabel) {
  if (DEFAULT_DIFF_KEYS.includes(key)) return t("diff." + key);
  return fallbackLabel;
}

/**
 * Translate a default hold color label. Returns translated label for known defaults,
 * or the original label for custom (admin-added) hold colors.
 */
export function tHoldLabel(t, key, fallbackLabel) {
  if (DEFAULT_HOLD_KEYS.includes(key)) return t("hold." + key);
  return fallbackLabel;
}

/**
 * The route-name label table (`{ diff, hold, unknownColor, unknownDifficulty }`,
 * the shape of the package's `ROUTE_LABELS.<lang>`) read through `t`, for the
 * shared `routeDisplayName`. Cached per `t` — `useTranslation` hands out one
 * `t` per language, and a route sort calls the name builder once per
 * comparison, so the fifteen lookups must not repeat on every call.
 */
const labelsByT = new WeakMap();
export function routeLabelsFor(t) {
  let labels = labelsByT.get(t);
  if (labels) return labels;
  labels = {
    diff: Object.fromEntries(DEFAULT_DIFF_KEYS.map((k) => [k, t("diff." + k)])),
    hold: Object.fromEntries(DEFAULT_HOLD_KEYS.map((k) => [k, t("hold." + k)])),
    unknownColor: t("route.unknownColor"),
    unknownDifficulty: t("route.unknownDifficulty"),
  };
  labelsByT.set(t, labels);
  return labels;
}
